import {
  createGtm,
  hasScript,
  loadScript,
  type GtmIdContainer,
  type LoadScriptOptions,
  type VueGtmUseOptions,
} from '@gtm-support/vue-gtm'
import { runAfterLoadWhenIdle } from '../utils/idle'
import { defineNuxtPlugin, useRouter, useRuntimeConfig } from '#app'

interface GTMConfigExtended {
  debug: boolean
  devtools: boolean
  enableRouterSync: boolean
  id: string
  source?: string
}

type GtmQueryParamInput = Partial<NonNullable<VueGtmUseOptions['queryParams']>>

export default defineNuxtPlugin((nuxtApp) => {
  if (import.meta.client) {
    const router = useRouter()
    const config = useRuntimeConfig()
    const options = config.public.gtm as GTMConfigExtended

    if (!options.id && !options.source) {
      console.warn('GTM ID is not configured')
      return
    }

    console.log('Initialising GTM...', options.id)

    const pluginOptions: VueGtmUseOptions = {
      ...options,
      vueRouter:
        options.enableRouterSync && router
          ? (router as VueGtmUseOptions['vueRouter'])
          : undefined,
      id: options.id,
      loadScript: false,
    }

    nuxtApp.vueApp.use(createGtm(pluginOptions))

    const loadGtmScript = () => {
      if (Array.isArray(pluginOptions.id)) {
        pluginOptions.id.forEach((id) => {
          if (typeof id === 'string') {
            loadScript(id, getLoadScriptOptions(pluginOptions))
            return
          }

          const config = getLoadScriptOptions(pluginOptions, {
            ...pluginOptions.queryParams,
            ...(id as GtmIdContainer).queryParams,
          })
          loadScript((id as GtmIdContainer).id, config)
        })
        return
      }

      const source =
        pluginOptions.source ?? 'https://www.googletagmanager.com/gtm.js'
      if (!hasScript(source)) {
        loadScript(pluginOptions.id, getLoadScriptOptions(pluginOptions))
      }
    }

    runAfterLoadWhenIdle(loadGtmScript, 3000, 8000)
  }
})

const getLoadScriptOptions = (
  options: VueGtmUseOptions,
  queryParams: GtmQueryParamInput | undefined = options.queryParams,
): LoadScriptOptions => ({
  ...options,
  defer: options.defer ?? false,
  compatibility: options.compatibility ?? false,
  queryParams: compactQueryParams(queryParams),
})

const compactQueryParams = (
  queryParams: GtmQueryParamInput | undefined,
): LoadScriptOptions['queryParams'] => {
  const compacted = Object.fromEntries(
    Object.entries(queryParams ?? {}).filter(
      ([, value]) => value !== undefined,
    ),
  )

  return Object.keys(compacted).length
    ? (compacted as unknown as LoadScriptOptions['queryParams'])
    : undefined
}
